import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, Menu, X, LayoutDashboard, LogOut, User, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import MegaMenu, { MegaMenuMobile } from './MegaMenu';

function LangToggle({ testIdSuffix = '' }) {
  const { lang, setLang } = useI18n();
  return (
    <button
      data-testid={`lang-toggle${testIdSuffix}`}
      onClick={() => setLang(lang === 'en' ? 'hi' : 'en')}
      className="inline-flex items-center gap-1.5 text-xs text-gray-300 hover:text-vault-gold transition px-2.5 py-1.5 rounded-full border border-white/10 hover:border-vault-gold/40"
      title={lang === 'en' ? 'Switch to Hindi' : 'अंग्रेज़ी में बदलें'}
    >
      <Globe className="w-3.5 h-3.5" />
      <span className="font-mono tracking-wider">{lang === 'en' ? 'EN' : 'हिं'}</span>
    </button>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full backdrop-blur-xl bg-[#0A1628]/70 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <div className="relative">
            <ShieldCheck className="w-7 h-7 text-vault-gold" strokeWidth={2.2} />
            <div className="absolute inset-0 blur-md bg-vault-gold/40 -z-10 group-hover:bg-vault-gold/60 transition" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-heading font-bold text-lg tracking-tight text-white">ValutDocu</span>
            <span className="text-[10px] tracking-[0.2em] uppercase text-vault-gold/80 font-mono">Secure Vault</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <MegaMenu />
          <Link data-testid="nav-ai" to="/ai-summarize" className="text-gray-300 hover:text-vault-gold transition inline-flex items-center gap-1">
            {t('nav.ai')}
            <span className="text-[9px] tracking-wider font-mono text-vault-gold border border-vault-gold/30 rounded px-1 py-0.5">NEW</span>
          </Link>
          <Link data-testid="nav-pricing" to="/pricing" className="text-gray-300 hover:text-vault-gold transition">{t('nav.pricing')}</Link>
          <Link data-testid="nav-blog" to="/blog" className="text-gray-300 hover:text-vault-gold transition">{t('nav.blog')}</Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LangToggle />
          {user && typeof user === 'object' ? (
            <>
              <Link
                data-testid="dashboard-link"
                to="/dashboard"
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-vault-gold transition"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span>{user.name || user.email.split('@')[0]}</span>
              </Link>
              {user.role === 'admin' && (
                <Link
                  data-testid="admin-link"
                  to="/admin"
                  className="text-xs text-vault-gold border border-vault-gold/40 rounded-full px-2.5 py-1 hover:bg-vault-gold/10 transition font-mono tracking-wider"
                >
                  ADMIN
                </Link>
              )}
              <button
                data-testid="logout-button"
                onClick={async () => { await logout(); navigate('/'); }}
                className="text-sm text-gray-400 hover:text-white transition"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link data-testid="login-link" to="/login" className="text-sm text-gray-300 hover:text-vault-gold transition">
                {t('nav.signin')}
              </Link>
              <Link
                data-testid="register-link"
                to="/register"
                className="text-sm bg-vault-gold text-vault-paper px-4 py-2 rounded-md font-semibold hover:bg-vault-gold-hover transition shadow-gold-glow"
              >
                {t('nav.signup')}
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-white/10 bg-[#0A1628]/95 px-6 py-4 space-y-3">
          <Link to="/" className="block text-gray-300 py-1">Home</Link>
          <Link to="/pricing" className="block text-gray-300 py-1">{t('nav.pricing')}</Link>
          <Link to="/blog" className="block text-gray-300 py-1">{t('nav.blog')}</Link>
          <Link to="/ai-summarize" className="block text-vault-gold font-medium py-1">{t('nav.ai')}</Link>
          <div className="pt-2">
            <p className="text-xs font-mono tracking-wider uppercase text-gray-500 mb-2">All Tools</p>
            <MegaMenuMobile onNavigate={() => setOpen(false)} />
          </div>
          <Link to="/split-pdf" className="block text-gray-300">Split PDF</Link>
          <Link to="/compress-pdf" className="block text-gray-300">Compress PDF</Link>
          <Link to="/pdf-to-word" className="block text-gray-300">PDF to Word</Link>
          <div className="h-px bg-white/10 my-3" />
          <div className="flex items-center gap-2"><LangToggle testIdSuffix="-mobile" /></div>
          <div className="h-px bg-white/10 my-3" />
          {user && typeof user === 'object' ? (
            <>
              <Link to="/dashboard" className="block text-vault-gold">{t('nav.dashboard')}</Link>
              {user.role === 'admin' && <Link to="/admin" className="block text-vault-gold">Admin Console</Link>}
              <button onClick={logout} className="block text-gray-400">{t('nav.signout')}</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block text-gray-300">{t('nav.signin')}</Link>
              <Link to="/register" className="block text-vault-gold font-semibold">{t('nav.signup')}</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
